import React, { useMemo, useState, useEffect } from "react";

import {
  Drawer,
  Image,
  Flex,
  Typography,
  Card,
  Radio,
  Checkbox,
  Space,
  Button,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import formatCurrency from "../helpers/formatCurrency";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import generateRandomId from "../helpers/generateId";
import {
  setOrderItem,
  clearSelectedItem,
  setToggleItemDetails,
} from "../redux/feature/order";
const { Text, Title } = Typography;

const groupAddons = (choices) => {
  const { choiceOnly, totalAddOnsPrice, variants_id } = Object.keys(
    choices
  ).reduce(
    (acc, key) => {
      const groupSelectedChoices = choices[key];

      if (key === "Variants") {
        acc.variants_id =
          groupSelectedChoices.length > 0
            ? groupSelectedChoices[0].choice_id
            : null;
      } else {
        groupSelectedChoices.forEach((item) => {
          acc.choiceOnly.push({
            choice_name: item.choice,
            choice_id: item.choice_id,
            choice_price: item.Choice_price,
          });
          acc.totalAddOnsPrice += parseFloat(item.Choice_price);
        });
      }

      return acc;
    },
    { choiceOnly: [], totalAddOnsPrice: 0, variants_id: null }
  );

  return { choiceOnly, totalAddOnsPrice, variants_id };
};

const ItemDetails = ({ triggerMessage }) => {
  const dispatch = useDispatch();

  const { selectedItem, toggleItemDetails } = useSelector(
    (state) => state.order
  );

  const [selectedChoices, setSelectedChoices] = useState({});
  const [quantity, setQuantity] = useState(selectedItem?.quantity || 1);
  const [missingRequiredGroups, setMissingRequiredGroups] = useState([]);
  const [variants, setVariants] = useState([]);

  // Compute the price only when necessary
  const computedPrice = useMemo(() => {
    return selectedItem.price_range
      ? selectedItem.price_range
      : Math.ceil(
          parseFloat(selectedItem.price) +
            (parseFloat(selectedItem.price) *
              parseFloat(selectedItem.mark_up_percentage)) /
              100
        );
  }, [
    selectedItem.price,
    selectedItem.price_range,
    selectedItem.mark_up_percentage,
  ]);

  const hasAddOns = selectedItem?.add_ons?.length > 0;

  useEffect(() => {
    setQuantity(selectedItem?.quantity || 1);
  }, [selectedItem]);

  useEffect(() => {
    try {
      const toParsed = selectedItem.Size || "[]";

      const parsedVariants = JSON.parse(toParsed);

      setVariants(parsedVariants);
    } catch (error) {
      console.log(">>> error parsing variants");
    }
  }, [selectedItem]);

  const handleRadioChange = (groupName, choiceName, choicePrice, choiceId) => {
    setSelectedChoices((prevChoices) => ({
      ...prevChoices,
      [groupName]: [
        {
          type: groupName,
          choice: choiceName,
          Choice_price: choicePrice,
          choice_id: choiceId,
        },
      ],
    }));
  };

  const handleCheckboxChange = (
    groupName,
    choiceName,
    choicePrice,
    choiceId
  ) => {
    setSelectedChoices((prevChoices) => {
      const currentChoices = prevChoices[groupName] || [];

      // If the choice is already selected, remove it
      if (currentChoices.some((item) => item.choice === choiceName)) {
        return {
          ...prevChoices,
          [groupName]: currentChoices.filter(
            (item) => item.choice !== choiceName
          ),
        };
      }

      // If it's not selected, add it to the selected choices
      return {
        ...prevChoices,
        [groupName]: [
          ...currentChoices,
          {
            choice: choiceName,
            Choice_price: choicePrice,
            type: groupName,
            choice_id: choiceId,
          },
        ],
      };
    });
  };

  const handleClose = () => {
    setSelectedChoices({});
    dispatch(setToggleItemDetails(false));
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncrement = () => {
    setQuantity(quantity + 1);
  };

  const handleAddItem = () => {
    const missingSelections = selectedItem.add_ons
      .filter((group) => group.group_isRequired === 1)
      .filter((group) => !selectedChoices[group.group_name]?.length);

    if (missingSelections.length > 0) {
      // Update state to highlight missing groups
      setMissingRequiredGroups(
        missingSelections.map((group) => group.group_name)
      );

      console.log(
        "Error: Missing required selections for the following groups:",
        missingSelections.map((group) => group.group_name)
      );
      return;
    }

    setMissingRequiredGroups([]);

    const addOnsSelected = groupAddons(selectedChoices);

    // Perform the add item action here
    const itemToAdd = {
      key: selectedItem?.toBeDeletedKey || generateRandomId(),
      id: selectedItem.id,
      image_name: selectedItem.image_url,
      partner_merchant_id: selectedItem.partner_merchant_id,
      price:
        (selectedItem.price === "0.00"
          ? (parseFloat(selectedItem.mark_up_percentage) *
              parseFloat(
                variants.find(
                  (dd) => dd.variantID === addOnsSelected?.variants_id
                )?.price
              )) /
              100 +
            parseFloat(
              variants.find(
                (dd) => dd.variantID === addOnsSelected?.variants_id
              )?.price
            )
          : (parseFloat(selectedItem.mark_up_percentage) *
              parseFloat(selectedItem.price)) /
              100 +
            parseFloat(selectedItem.price)) +
        parseFloat(addOnsSelected?.totalAddOnsPrice),
      quantity,
      sub_title: selectedItem.sub_title,
      title: selectedItem.title,
      variant:
        selectedItem.price === "0.00"
          ? variants.find((dd) => dd.variantID === addOnsSelected?.variants_id)
              ?.variant
          : null,
      merchantItem: true,
      mark_up_percentage: selectedItem.mark_up_percentage,
      original_price:
        selectedItem.price === "0.00"
          ? parseFloat(
              variants.find(
                (dd) => dd.variantID === addOnsSelected?.variants_id
              )?.price
            )
          : parseFloat(selectedItem.price),
      markup_value:
        selectedItem.price === "0.00"
          ? (
              (parseFloat(selectedItem.mark_up_percentage) *
                parseFloat(
                  variants.find(
                    (dd) => dd.variantID === addOnsSelected?.variants_id
                  )?.price
                )) /
              100
            ).toFixed(2)
          : (
              (parseFloat(selectedItem.price) *
                parseFloat(selectedItem.mark_up_percentage)) /
              100
            ).toFixed(2),
      total_markup_value:
        selectedItem.price === "0.00"
          ? (
              ((parseFloat(selectedItem.mark_up_percentage) *
                parseFloat(
                  variants.find(
                    (dd) => dd.variantID === addOnsSelected?.variants_id
                  )?.price
                )) /
                100) *
              quantity
            ).toFixed(2)
          : (
              ((parseFloat(selectedItem.price) *
                parseFloat(selectedItem.mark_up_percentage)) /
                100) *
              quantity
            ).toFixed(2),
      add_ons: addOnsSelected?.choiceOnly,
      total_add_ons_price: addOnsSelected?.totalAddOnsPrice,
    };

    dispatch(
      setOrderItem({
        itemToAdd,
        selectedItem,
        key: selectedItem?.toBeDeletedKey || null,
      })
    );
    dispatch(clearSelectedItem());

    setSelectedChoices({});
    setQuantity(1);
    setMissingRequiredGroups([]);
    setVariants([]);

    dispatch(setToggleItemDetails(false));
    triggerMessage(selectedItem?.toBeDeletedKey);
  };

  return (
    <Drawer
      destroyOnClose
      placement="bottom"
      title="Item Details"
      onClose={handleClose}
      open={toggleItemDetails}
      size="large"
      height="100%"
    >
      <div style={{ paddingBottom: "70px" }}>
        <div style={{ textAlign: "center", margin: "auto 0" }}>
          <Image
            width={"100%"}
            src={`https://groceria-storage.sgp1.cdn.digitaloceanspaces.com/assets/images/partner-merchant-menu/${selectedItem.image_url}`}
            alt={selectedItem.title}
            style={{ borderRadius: "22px" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <div style={{ marginTop: "0px", textAlign: "center" }}>
            <div style={{ fontWeight: "bold", fontSize: "20px" }}>
              {selectedItem.title}
            </div>
            <p style={{ fontSize: "16px" }}>Total: ₱{computedPrice}</p>
          </div>
        </div>

        <br />
        <div>
          {hasAddOns && (
            <>
              {selectedItem?.add_ons.map((item) => {
                const isMissing = missingRequiredGroups.includes(
                  item.group_name
                );

                return (
                  <Card
                    key={item.group_name}
                    style={{
                      border: isMissing ? "2px solid red" : "1px solid #e0e0e0",
                      borderRadius: "22px",
                    }}
                  >
                    <Flex align="center" justify="space-between">
                      <div>
                        <p style={{ fontWeight: "bold", margin: 0 }}>
                          {item.group_type === "add_ons"
                            ? `Add-ons ${item.group_name} `
                            : `Choose ${item.group_name}`}
                        </p>
                        <p
                          variant="labelMedium"
                          style={{ color: "gray", margin: 0 }}
                        >
                          {item.group_max_selection === 1
                            ? "Select one"
                            : `Select up to ${item.group_max_selection} options`}
                        </p>
                      </div>
                      <div>
                        <Text
                          style={{
                            backgroundColor:
                              item.group_name === "Variants"
                                ? "black"
                                : item.group_isRequired
                                ? "#00BF63"
                                : "#c0c0c0",
                            fontWeight: "bold",
                            color: "#fff",
                            padding: 5,
                            fontSize: 10,
                            borderRadius: 20,
                          }}
                        >
                          {item.group_isRequired === 1
                            ? "Required"
                            : "Optional"}
                        </Text>
                      </div>
                    </Flex>
                    {/* choices */}
                    {item.choices.map((choice, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 8,
                        }}
                      >
                        <Space align="center" style={{ flex: 1, minWidth: 0 }}>
                          {item.group_max_selection === 1 ? (
                            <Radio
                              value={choice.choice_name}
                              checked={selectedChoices[item.group_name]?.some(
                                (selected) =>
                                  selected.choice === choice.choice_name
                              )}
                              onChange={() =>
                                handleRadioChange(
                                  item.group_name,
                                  choice.choice_name,
                                  choice.choice_price,
                                  choice.choice_id
                                )
                              }
                            />
                          ) : (
                            <Checkbox
                              checked={selectedChoices[item.group_name]?.some(
                                (selected) =>
                                  selected.choice === choice.choice_name
                              )}
                              disabled={
                                selectedChoices[item.group_name]?.length >=
                                  item.group_max_selection &&
                                !selectedChoices[item.group_name]?.some(
                                  (selected) =>
                                    selected.choice === choice.choice_name
                                )
                              }
                              onChange={() =>
                                handleCheckboxChange(
                                  item.group_name,
                                  choice.choice_name,
                                  choice.choice_price,
                                  choice.choice_id
                                )
                              }
                            />
                          )}
                          <Text
                            style={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {choice.choice_name}
                          </Text>
                        </Space>
                        <Text>
                          {parseFloat(choice.choice_price) === 0
                            ? "Free"
                            : `${item.group_name === "Variants" ? "" : "+"}
                            ${formatCurrency(parseFloat(choice.choice_price))}`}
                        </Text>
                      </div>
                    ))}
                    {/*  */}
                  </Card>
                );
              })}
            </>
          )}
        </div>
        <div
          style={{
            position: "fixed",
            bottom: "0",
            left: "0",
            width: "90%",
            backgroundColor: "#fff",
            padding: "15px 20px",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Button
              shape="circle"
              icon={<MinusOutlined />}
              onClick={handleDecrement}
            />
            <div style={{ fontSize: "18px", fontWeight: "bold" }}>
              {quantity}
            </div>
            <Button
              shape="circle"
              icon={<PlusOutlined />}
              onClick={handleIncrement}
            />
          </div>

          <Button
            type="primary"
            size="large"
            style={{
              background: "#00BF63",
              fontWeight: "bold",
              width: "100%",
            }}
            onClick={handleAddItem}
          >
            {selectedItem?.toBeDeletedKey ? "Confirm" : "Add Item"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default ItemDetails;
